// Soft discovery is deliberately separate from plans/entitlements: every
// destination remains available. A level just introduces another study tool.
export type FeatureDiscovery = {
    id: string;
    level: number;
    title: string;
    message: string;
    path: string;
    requiresLevel?: boolean;
};

export const FEATURE_DISCOVERIES: readonly FeatureDiscovery[] = [
    { id: "kana-writing", level: 2, title: "Kana writing practice", message: "Try drawing hiragana and katakana stroke by stroke.", path: "/kana-writing-practice" },
    { id: "vocabulary", level: 3, title: "Vocabulary practice", message: "Build your Japanese word collection one word at a time.", path: "/vocabulary" },
    { id: "grammar", level: 4, title: "Grammar guides", message: "Explore grammar patterns with clear examples and explanations.", path: "/grammar" },
    { id: "kanji-memory-quiz", level: 5, title: "Kanji memory quiz", message: "Put your kanji recall to the test with a focused quiz.", path: "/kanji-memory-quiz", requiresLevel: true },
    { id: "speaking", level: 6, title: "Speaking practice", message: "Practice saying Japanese aloud when your device is ready.", path: "/speaking-practice", requiresLevel: true },
];

// Core study paths remain available from the start. These are the two optional
// practice tools introduced as a small, reversible first level-gating trial.
export function gatedFeatureForPath(path: string): FeatureDiscovery | undefined {
    const normalized = path.replace(/^\/pt(?=\/)/, "");
    return FEATURE_DISCOVERIES.find((discovery) => discovery.requiresLevel && discovery.path === normalized);
}

export function isFeatureUnlocked(discovery: FeatureDiscovery, level: number): boolean {
    return !discovery.requiresLevel || (Number.isFinite(level) && Math.floor(level) >= discovery.level);
}

// The profile can preview what comes next without hiding or restricting any
// feature. Invalid values conservatively start at the first introduction.
export function nextFeatureDiscovery(level: number): FeatureDiscovery | undefined {
    const currentLevel = Number.isFinite(level) ? Math.floor(level) : 0;
    return FEATURE_DISCOVERIES.find((discovery) => discovery.level > currentLevel);
}

// No first-load backlog: only show discoveries actually crossed by a newly
// resolved level change. Multi-level gains retain catalogue order.
export function discoveriesCrossedByLevel(previousLevel: number, nextLevel: number): FeatureDiscovery[] {
    if (!Number.isFinite(previousLevel) || !Number.isFinite(nextLevel) || nextLevel <= previousLevel) return [];
    return FEATURE_DISCOVERIES.filter(({ level }) => level > previousLevel && level <= nextLevel);
}
